import _ from "lodash";
import React from "react";
import Select from "react-select";

import { TypeSelectorOptions } from "../types";
import { themedSelectStyles } from "@/components/Dropdown/themedSelectStyles";
import { usePortalContainer } from "@/design-system/portalContainer";

const typesMap = _.keyBy(TypeSelectorOptions, "value");

type ConstType = { label: React.ReactNode; value: string } | null;
type SetConstStrFn = (constStr: string) => void;
type SetConstTypeFn = (constType: ConstType | null) => void;

export const SendConstantPropertyInputs: React.FC<{
  constStr: string;
  setConstStr: SetConstStrFn;
  constType: ConstType;
  setConstType: SetConstTypeFn;
  operandValue: string;
  setOperandValue: (operandValue: string) => void;
}> = ({
  constStr,
  setConstStr,
  constType,
  setConstType,
  operandValue,
  setOperandValue,
}) => {
  const portalContainer = usePortalContainer();
  return (
    <>
      <input
        className="mt-2 w-full rounded border px-2 py-2 font-mono tracking-tight"
        placeholder="Property Name"
        value={operandValue}
        onChange={(e) => setOperandValue(e.target.value)}
      />
      <input
        className="mt-2 w-full rounded border px-2 py-2 font-mono tracking-tight"
        placeholder="Property Value"
        value={constStr}
        onChange={(e) => setConstStr(e.target.value)}
      />
      <Select
        styles={themedSelectStyles<NonNullable<ConstType>>({
          menuPortal: (base) => ({ ...base, zIndex: "var(--z-popover)" }),
        })}
        className="mt-2"
        placeholder="Constant Type"
        value={constType}
        options={TypeSelectorOptions}
        onChange={(o, _) => setConstType(o)}
        menuPortalTarget={portalContainer}
        menuPosition="fixed"
      />
    </>
  );
};

export function initSendConstPropInputState(
  targetValue: string,
  setConstStr: SetConstStrFn,
  setConstType: SetConstTypeFn,
) {
  let parsedTargetValue = "";
  try {
    parsedTargetValue = JSON.parse(targetValue);
  } catch (error) {
    if (process.env.NODE_ENV !== "test") {
      console.error(
        "Failed to parse json from targetValue - ",
        targetValue,
        " error - ",
        error,
      );
    }
  }

  let targetType = typeof parsedTargetValue;
  if (
    targetType !== "number" &&
    targetType !== "boolean" &&
    targetType !== "string"
  ) {
    targetType = "string";
  }

  setConstStr(String(parsedTargetValue));
  setConstType(typesMap[targetType]);
}

export function getSendConstPropTargetValue(
  constStr: string,
  constType: ConstType,
) {
  // @ts-ignore
  const typeKey: string = (constType && constType.value) || "string";

  let obj;
  if (typeKey === "number") {
    obj = Number(constStr);
    if (Number.isNaN(obj)) {
      obj = 0;
    }
  } else if (typeKey === "boolean") {
    obj = stringToBoolean(constStr);
  } else {
    obj = constStr;
  }

  return JSON.stringify(obj);
}

function stringToBoolean(s: string): boolean {
  s = s.toLowerCase();
  if (s === "t" || s === "true") {
    return true;
  }
  return false;
}
